import React, { useEffect, useState } from 'react';
import {
    DialogActions,
    DialogContentText,
    DialogContent,
    DialogTitle,
    Dialog,
    Alert,
    Box,
    Grid2,
    TextField,
    CircularProgress,
    Typography,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { useGetOrders } from '@openapi/orders/orders';
import { formatCurrency } from '../pages/bookings/helpers/formatters';

const RefundConfirmationModal = ({
    title,
    content,
    bookingId,
    confirm,
    showModal,
    MODAL_TYPE,
    defaultAmount,
}) => {
    const { t } = useTranslation();
    const closeAction = () => showModal(MODAL_TYPE.NONE);

    // refund amount input
    const [inputValue, setInputValue] = useState(0);
    const minValue = 0;
    const [maxValue, setMaxValue] = useState(100); // set to full booking amount (booking items total price) later
    const handleInputChange = (event) => {
        const value = event.target.value === '' ? 0 : Number(event.target.value * 100);
        setInputValue(value);
    };
    const handleBlur = () => {
        if (inputValue < minValue) {
            setInputValue(minValue);
        } else if (inputValue > maxValue) {
            setInputValue(maxValue);
        }
    };

    const {
        data,
        isLoading: loading,
        error,
    } = useGetOrders({
        optionalId: bookingId,
        pageSize: 100,
        includeTransactions: true,
    });

    // set maxValue after data is loaded for the first time
    useEffect(() => {
        // calculate the maximum amount that can be refunded
        let refundingMaxAmount = 0;
        const refundingOrders = [];
        if (data && Array.isArray(data.orders)) {
            data.orders.forEach((order) => {
                order.transactions.forEach((trans) => {
                    refundingMaxAmount += trans.amount;
                });
                refundingOrders.push({ id: order.id, amount: order.amount });
            });
            setMaxValue(refundingMaxAmount);
            setInputValue(
                defaultAmount != null
                    ? Math.min(defaultAmount, refundingMaxAmount)
                    : refundingMaxAmount,
            );
        }
    }, [data, defaultAmount]);

    const showRefundInput = (
        <Grid2 container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" id="input-refund">
                    {t('refundChunks.refundAmount')}:
                </Typography>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <TextField
                    value={inputValue / 100} // convert cents to dollars for display
                    type="number"
                    size="small"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    inputProps={{
                        step: 1,
                        min: minValue,
                        max: maxValue,
                        type: 'number',
                        'aria-labelledby': 'input-refund',
                    }}
                    InputProps={{
                        startAdornment: (
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                    marginRight: 4,
                                    color: '#888',
                                }}
                            >
                                {formatCurrency(inputValue / 100).replace(/[0-9.,\s]+/, '')}
                            </span>
                        ),
                    }}
                />
            </Grid2>
        </Grid2>
    );

    let extraContent = null;
    let confirmButtonText = t('common.refund');

    if (loading) {
        extraContent = (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
            </Box>
        );
    } else if (error) {
        extraContent = (
            <Alert severity="error" sx={{ my: 2 }}>
                {t('common.errorLoadingData')}
            </Alert>
        );
    } else {
        extraContent = (
            <DialogContentText component="div" id="alert-dialog-refund-content">
                {t('bookings.refundBookingWithMaxAmount', {
                    refundValue: formatCurrency(maxValue / 100),
                })}
                {showRefundInput}
            </DialogContentText>
        );
        confirmButtonText = t('bookings.refundBookingWithAmount', {
            refundValue: formatCurrency(inputValue / 100),
        });
    }

    return (
        <Dialog
            open
            onClose={closeAction}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-content">{content}</DialogContentText>
                {extraContent}
            </DialogContent>
            <DialogActions>
                <CommonButton
                    onClick={closeAction}
                    autoFocus
                    label={t('common.close')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={() => {
                        confirm(bookingId, inputValue);
                        showModal(MODAL_TYPE.NONE);
                    }}
                    label={confirmButtonText}
                    buttonType="primary"
                    disabled={inputValue <= 0 || inputValue > maxValue}
                />
            </DialogActions>
        </Dialog>
    );
};

export default RefundConfirmationModal;
